import { CHANNEL_KEYS, CHANNEL_LABELS, type ChannelKey } from "@theadam/shared";
import { Badge } from "@/components/ui/misc";
import { useMe } from "@/lib/auth";
import { cn } from "@/lib/utils";

/** Kanal logoları public/channels altında (markaların resmi simgeleri). */
const LOGO_SRC: Record<ChannelKey, string> = {
  ideasoft: "/channels/ideasoft.svg",
  trendyol: "/channels/trendyol.png",
  hepsiburada: "/channels/hepsiburada.svg",
  woocommerce: "/channels/woocommerce.svg",
  ticimax: "/channels/ticimax.svg",
  amazon: "/channels/amazon.svg",
  pazarama: "/channels/pazarama.svg",
  shopify: "/channels/shopify.svg",
};

const SIZES = { xs: "size-4 rounded", sm: "size-5 rounded-md", md: "size-7 rounded-md", lg: "size-10 rounded-lg" } as const;

export function ChannelLogo({ channel, size = "sm", className }: { channel: ChannelKey; size?: keyof typeof SIZES; className?: string }) {
  return <img src={LOGO_SRC[channel]} alt={CHANNEL_LABELS[channel]} title={CHANNEL_LABELS[channel]} className={cn("shrink-0 object-cover", SIZES[size], className)} />;
}

/** Logo + kanal adı (+ demo etiketi). Listelerde kanalı tek bakışta ayırt etmek için. */
export function ChannelTag({ channel, name, mode, className }: { channel: ChannelKey; name?: string; mode?: "demo" | "live"; className?: string }) {
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-2", className)}>
      <ChannelLogo channel={channel} size="sm" />
      <span className="truncate font-medium">{name ?? CHANNEL_LABELS[channel]}</span>
      {mode === "demo" && <Badge tone="demo">DEMO</Badge>}
    </span>
  );
}

/** Konum ekiyle kanal adı ("Trendyol'da", "IdeaSoft'ta"). */
export const CHANNEL_IN: Record<ChannelKey, string> = { ideasoft: "IdeaSoft'ta", trendyol: "Trendyol'da", hepsiburada: "Hepsiburada'da", woocommerce: "WooCommerce'te", ticimax: "Ticimax'ta", amazon: "Amazon'da", pazarama: "Pazarama'da", shopify: "Shopify'da" };

/**
 * Bağlı kanallar (kurulumu tamamlanmış, durdurulmamış), sabit kanal sırasıyla. keep: o an seçili kanal
 * bağlı olmasa da listede kalır (eski bağlantılar ve açık süzgeç boş kalmasın). Oturum bilgisi henüz
 * gelmediyse liste boştur.
 */
export function useConnectedChannels(keep?: ChannelKey): ChannelKey[] {
  const { data: me } = useMe();
  return CHANNEL_KEYS.filter((k) => k === keep || me?.connections.channels.includes(k));
}

/**
 * Kanal ayrımı: "Tüm kanallar" (birleşik) veya tek kanal. Genel Bakış, Raporlar,
 * Ürünler ve Siparişlerde aynı görünüm.
 */
export function ChannelFilter({ value, onChange, className }: { value: ChannelKey | undefined; onChange: (v: ChannelKey | undefined) => void; className?: string }) {
  const item = (active: boolean) =>
    cn("inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm whitespace-nowrap", active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground");
  const channels = useConnectedChannels(value);
  // Tek kanal bağlıysa ayrım anlamsız: süzgeç gösterilmez.
  if (channels.length < 2 && !value) return null;
  return (
    <div role="radiogroup" aria-label="Kanal" className={cn("flex rounded-lg border bg-card p-1", className)}>
      <button type="button" role="radio" aria-checked={!value} onClick={() => onChange(undefined)} className={item(!value)}>
        Tüm kanallar
      </button>
      {channels.map((k) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)} className={item(value === k)}>
          <ChannelLogo channel={k} size="xs" />
          {CHANNEL_LABELS[k]}
        </button>
      ))}
    </div>
  );
}

export function parseChannel(v: string | null): ChannelKey | undefined {
  return v && (CHANNEL_KEYS as readonly string[]).includes(v) ? (v as ChannelKey) : undefined;
}
